import { FieldProps, Field } from 'formik'
import {
  makeStyles,
  Theme,
  createStyles,
  Typography,
  Box,
  IconButton,
} from '@material-ui/core'
import { AttachFile, Clear } from '@material-ui/icons'
import { ChangeEvent, useRef, useState } from 'react'
import { AccordionDetailsTitle } from '../Accordion/CustomAccordion'
import CustomTranslations from '../../utils/lang/CustomTranslations'
import CustomButton from '../Button/CustomButton'

interface StylesProps {
  color: string
  maxwidth?: string
}

const useStyles = makeStyles<Theme, StylesProps>(() =>
  createStyles({
    root: (props) => ({
      maxWidth: props.maxwidth || '400px',
      display: 'flex',
      alignItems: 'center',
      paddingTop: '10px',
    }),
    input: {
      display: 'none',
    },
    fileName: {
      marginTop: '8px',
      fontSize: '14px',
      flexGrow: 1,
      marginLeft: '8px',
    },
    clearButton: {
      marginLeft: '8px',
    },
    errorText: {
      color: 'red',
      fontSize: '12px',
      marginTop: '4px',
    },
  })
)

interface FormikFileUploadProps {
  name: string
  title?: string
  maxwidth?: string
  lightTheme?: boolean
  tooltip?: string
  allowedTypes?: string[]
  maxFileSize?: number
}

const FormikFileUpload = ({
  name,
  title,
  maxwidth,
  lightTheme,
  tooltip,
  allowedTypes = ['application/pdf'],
  maxFileSize = 5,
}: FormikFileUploadProps): JSX.Element => {
  const maxSizeBytes = maxFileSize * 1024 * 1024
  const styleProps: StylesProps = {
    color: lightTheme ? 'white' : '#1976D2',
    maxwidth,
  }
  const classes = useStyles(styleProps)
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleButtonClick = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <Field name={name}>
      {({ form }: FieldProps): JSX.Element => {
        const handleFileChange = (
          event: ChangeEvent<HTMLInputElement>
        ): void => {
          const file = event.currentTarget.files?.[0]
          if (file) {
            if (!allowedTypes.includes(file.type)) {
              setError(`Invalid file type. Allowed: ${allowedTypes.join(', ')}`)
              form.setFieldValue(name, null)
              return
            }

            if (file.size > maxSizeBytes) {
              setError(`File is too large. Max size: ${maxFileSize}MB`)
              form.setFieldValue(name, null)
              return
            }

            setError(null)
            form.setFieldValue(name, file)
          }
        }

        const handleClearFile = (): void => {
          form.setFieldValue(name, null)
          if (fileInputRef.current) {
            fileInputRef.current.value = '' // Reset the input value as well
          }
          setError(null)
        }

        return (
          <Box className={classes.root}>
            {title && (
              <AccordionDetailsTitle
                title={title}
                tooltip={tooltip}
                variant="h2"
                margin="0"
              />
            )}
            <input
              ref={fileInputRef}
              accept={allowedTypes.join(',')}
              className={classes.input}
              id={`file-upload-${name}`}
              type="file"
              onChange={handleFileChange}
            />
            <CustomButton
              fontcolor="white"
              variant="contained"
              startIcon={<AttachFile />}
              onClick={handleButtonClick}
            >
              {CustomTranslations.ATTACH_FILE}
            </CustomButton>
            {form.values[name] && (
              <Typography className={classes.fileName}>
                {form.values[name].name}
              </Typography>
            )}
            {form.values[name] && (
              <IconButton
                className={classes.clearButton}
                onClick={handleClearFile}
                aria-label="clear"
              >
                <Clear />
              </IconButton>
            )}
            {error && (
              <Typography className={classes.errorText}>{error}</Typography>
            )}
          </Box>
        )
      }}
    </Field>
  )
}

export default FormikFileUpload
